/**
 * Publishes `--app-height` and `--app-top` so `.app-shell` fills the
 * visible app area in Chrome — iPhone webpage tab, Windows Chrome, and
 * Add-to-Home-Screen. Chip uses Chrome only; do not special-case Safari.
 *
 * Two modes (ported from the Carvoeiro v10 hotfix — do not collapse them):
 *
 * 1. Standalone / A2HS (`isStandaloneDisplay`): visualViewport height +
 *    offsetTop. There is no browser toolbar; VV is the visible app box.
 *
 * 2. Chrome webpage (iPhone tab and Windows): `top = 0`,
 *    `height = Math.max(innerHeight, vv.height + offsetTop)`.
 *    visualViewport *alone* undershoots on Chrome iOS and leaves a cream
 *    (`--color-sand`) gap above the toolbar. Taking the larger of
 *    innerHeight and the VV extent fills down to the toolbar. Desktop
 *    Chrome typically has innerHeight ≈ VV, so this is a no-op there.
 *
 * `.app-shell` uses `top: var(--app-top); bottom: 0; height` and
 * `max-height: var(--app-height)`. The nav stays an in-flow flex footer.
 * Do not reintroduce `viewport-fit=cover` without on-device proof.
 */

const HEIGHT_VAR = '--app-height';
const TOP_VAR = '--app-top';

export interface AppFrame {
  height: number;
  top: number;
}

export interface VisualViewportLike {
  height: number;
  offsetTop: number;
  scale?: number;
}

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * Pure frame picker. `standalone` is passed in so tests can pin Chrome
 * webpage vs A2HS without jsdom's missing matchMedia/visualViewport.
 *
 * Pinch-zoom (`scale !== 1`) returns null: leave the last chrome-sized
 * frame in place so the shell does not collapse into the pinch rectangle.
 */
export function computeAppFrame(
  visualViewport: VisualViewportLike | null | undefined,
  innerHeight: number,
  standalone: boolean,
): AppFrame | null {
  if (visualViewport && visualViewport.scale != null && visualViewport.scale !== 1) {
    return null;
  }

  const inner = Math.round(innerHeight) || 0;
  const vvH = visualViewport && visualViewport.height > 0 ? visualViewport.height : 0;
  const vvTop = visualViewport ? Math.max(0, visualViewport.offsetTop) : 0;

  if (standalone) {
    if (vvH > 0) {
      return {
        height: Math.round(vvH),
        top: Math.round(vvTop) || 0,
      };
    }
    if (inner <= 0) return null;
    return { height: inner, top: 0 };
  }

  // Chrome webpage (iPhone tab + Windows): VV-only is too short on iPhone.
  const vvExtent = vvH > 0 ? Math.round(vvH + vvTop) : 0;
  const h = Math.max(inner, vvExtent);
  if (h <= 0) return null;
  return { height: h, top: 0 };
}

function measure(): AppFrame | null {
  const vv = window.visualViewport;
  return computeAppFrame(
    vv ? { height: vv.height, offsetTop: vv.offsetTop, scale: vv.scale } : null,
    window.innerHeight,
    isStandaloneDisplay(),
  );
}

function apply(): void {
  const frame = measure();
  if (!frame) return;
  const root = document.documentElement.style;
  root.setProperty(HEIGHT_VAR, `${frame.height}px`);
  root.setProperty(TOP_VAR, `${frame.top}px`);
}

/**
 * Starts publishing the value. Returns a teardown function.
 *
 * Orientation changes are re-read on a short delay as well as immediately:
 * iOS fires `orientationchange` before it has finished resizing, so the
 * immediate read can still return the old dimensions. visualViewport
 * `scroll` is required too — offsetTop moves when browser chrome animates
 * without a window resize.
 */
export function initAppHeight(): () => void {
  apply();
  requestAnimationFrame(apply);

  const onResize = () => {
    apply();
    requestAnimationFrame(apply);
  };
  const onOrientation = () => {
    apply();
    window.setTimeout(apply, 250);
  };

  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onOrientation);
  window.visualViewport?.addEventListener('resize', onResize);
  window.visualViewport?.addEventListener('scroll', onResize);
  document.addEventListener('visibilitychange', onResize);

  return () => {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onOrientation);
    window.visualViewport?.removeEventListener('resize', onResize);
    window.visualViewport?.removeEventListener('scroll', onResize);
    document.removeEventListener('visibilitychange', onResize);
  };
}

/** Exposed for the ?debug=viewport readout and for tests. */
export function readAppHeight(): { measured: number; cssVar: string; top: string } {
  const frame = measure();
  return {
    measured: frame?.height ?? 0,
    cssVar: document.documentElement.style.getPropertyValue(HEIGHT_VAR),
    top: document.documentElement.style.getPropertyValue(TOP_VAR),
  };
}
